(() => {
  'use strict';
  if (customElements.get('pdl-project-purchase')) return;
  class ProjectPurchase extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.button = this.querySelector('[data-project-buy]');
      this.quantity = this.querySelector('[data-project-quantity]');
      this.message = this.querySelector('[data-project-message]');
      this.config = JSON.parse(this.querySelector('[data-project-config]').textContent);
      this.root = window.Shopify?.routes?.root || '/';
      this.button.disabled = !this.valid(this.config, 1);
      if (this.button.disabled) this.notify(this.config.messages.unavailable);
      this.querySelector('[data-project-form]').addEventListener('submit', event => this.add(event));
      this.querySelector('[data-project-personalize]')?.addEventListener('click', event => this.personalize(event));
    }
    async personalize(event) {
      event.preventDefault();
      if (this.personalizing) return;
      this.personalizing = true;
      const link = event.currentTarget;
      link.setAttribute('aria-busy','true');
      try {
        const projects = window.PdlMSBCartProjects;
        if (!projects) throw Error(this.config.messages.error);
        const cart = await projects.read(), groups = projects.groups(cart,this.dataset.projectId);
        let selected = groups.length === 1 ? groups[0] : null;
        if (groups.length > 1) {
          let select = this.querySelector('[data-project-edit-choice]');
          if (!select) {
            const label = document.createElement('label');
            label.textContent = projects.messages().choose;
            label.style.cssText = 'display:grid;gap:8px;font-size:14px';
            select = document.createElement('select');
            select.dataset.projectEditChoice = '';
            select.style.cssText = 'min-height:44px;padding:8px;width:100%;font:inherit';
            select.add(new Option('—',''));
            groups.forEach((group,index) => select.add(new Option(projects.messages().projectOption.replace('{index}',index+1).replace('{count}',projects.groupCount(group.lines)),group.id)));
            select.add(new Option(projects.messages().newProject,'new'));
            label.append(select);link.before(label);select.focus();
            return;
          }
          if (!select.value) {select.focus();return;}
          selected = groups.find(group => group.id === select.value);
          if (select.value !== 'new' && !selected) throw Error(projects.messages().changed);
        }
        const url = new URL(link.href, location.origin);
        url.searchParams.set('msb_project',this.dataset.projectId);
        url.searchParams.set('msb_name',this.dataset.projectTitle);
        if (selected) {
          url.searchParams.set('msb_edit',selected.id);
          const ratios = this.config.items.map(item => selected.lines.filter(line => line.variant_id === item.id).reduce((sum,line) => sum+line.quantity,0)/item.quantity);
          const inferred = ratios.length && ratios.every(n => n === ratios[0] && Number.isInteger(n) && n > 0) ? ratios[0] : 1;
          url.searchParams.set('msb_count',projects.groupCount(selected.lines,inferred));
          const saved = projects.savedSnapshot(selected.lines,selected.id);
          if (saved) url.searchParams.set('msb',projects.encode(saved));
        } else {url.searchParams.delete('msb_edit');url.searchParams.delete('msb_count');}
        const preview = new URL(location.href).searchParams.get('preview_theme_id');
        if (preview) url.searchParams.set('preview_theme_id',preview);
        location.assign(url.href);
      } catch (error) {this.notify(error.message || this.config.messages.error);}
      finally {this.personalizing=false;link.removeAttribute('aria-busy');}
    }
    valid(config, count, cart = {items: []}) {
      const ids = new Set();
      return Number.isInteger(count) && count > 0 && config.expected > 0 &&
        config.items.length === config.expected && config.items.every(item => {
          if (!Number.isSafeInteger(item.id) || ids.has(item.id) || !Number.isInteger(item.quantity) || item.quantity < 1) return false;
          ids.add(item.id);
          const requested = item.quantity * count;
          const existing = cart.items.filter(line => line.variant_id === item.id).reduce((sum, line) => sum + line.quantity, 0);
          return item.available && requested >= item.minimum && requested % item.increment === 0 &&
            (!item.maximum || existing + requested <= item.maximum) &&
            (!item.tracked || item.policy === 'continue' || existing + requested <= item.stock);
        });
    }
    notify(text) {
      this.message.textContent = text;
      this.message.hidden = !text;
    }
    async read(url) {
      const response = await fetch(url, {credentials: 'same-origin', cache: 'no-store'});
      if (!response.ok) throw Error(this.config.messages.error);
      return response;
    }
    async recover(operation, items) {
      const cart = await (await this.read(this.root + 'cart.js')).json();
      const own = cart.items.filter(line => line.properties?._MSB_compra === operation);
      const complete = items.every(item => own.filter(line => line.variant_id === item.id).reduce((sum, line) => sum + line.quantity, 0) === item.quantity);
      if (complete) return true;
      if (own.length) {
        const updates = Object.fromEntries(own.map(line => [line.key, 0]));
        const response = await fetch(this.root + 'cart/update.js', {method:'POST',credentials:'same-origin',headers:{'Content-Type':'application/json'},body:JSON.stringify({updates})});
        if (!response.ok) throw Error(this.config.messages.review);
      }
      return false;
    }
    async add(event) {
      event.preventDefault();
      if (this.busy) return;
      const count = Number(this.quantity.value);
      if (!this.querySelector('form').reportValidity()) return;
      this.busy = true;
      this.button.disabled = true;
      this.button.textContent = this.config.messages.adding;
      this.notify('');
      const operation = crypto.randomUUID();
      let attempted = false;
      let items = [];
      try {
        const source = new URL(location.href);
        source.searchParams.set('section_id', this.dataset.sectionId);
        const [html, cart] = await Promise.all([
          this.read(source.href).then(response => response.text()),
          this.read(this.root + 'cart.js').then(response => response.json())
        ]);
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fresh = doc.querySelector(`pdl-project-purchase[data-project-id="${this.dataset.projectId}"] [data-project-config]`);
        if (!fresh) throw Error(this.config.messages.error);
        const config = JSON.parse(fresh.textContent);
        if (!this.valid(config, count, cart)) throw Error(this.config.messages.unavailable);
        items = config.items.map(item => ({id:item.id,quantity:item.quantity * count,properties:{_Projeto:this.dataset.projectTitle,_Projeto_id:this.dataset.projectId,_MSB_compra:operation,_MSB_quantidade:String(count)}}));
        const projects = window.PdlMSBCartProjects;
        const shared = this.querySelector('[data-project-personalize]')?.href;
        if (projects && shared) {
          const snapshot = projects.decode(new URL(shared).searchParams.get('msb'));
          Object.assign(items[0].properties,projects.receipt(snapshot));
          projects.remember(operation,snapshot);
        }
        attempted = true;
        const response = await fetch(this.root + 'cart/add.js', {method:'POST',credentials:'same-origin',headers:{'Content-Type':'application/json'},body:JSON.stringify({items})});
        if (!response.ok) {
          const data = await response.json();
          throw Error(data.description || this.config.messages.error);
        }
        location.assign(this.root + 'cart');
      } catch (error) {
        if (attempted) {
          try {
            if (await this.recover(operation, items)) {
              location.assign(this.root + 'cart');
              return;
            }
          } catch {
            this.notify(this.config.messages.review);
            this.button.textContent = this.config.messages.buy;
            return;
          }
        }
        this.notify(error.message || this.config.messages.error);
        this.busy = false;
        this.button.disabled = false;
        this.button.textContent = this.config.messages.buy;
      }
    }
  }
  customElements.define('pdl-project-purchase', ProjectPurchase);
})();

